var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDiff } from '@/ducks/util/diff';
/**
 * A function that given a function that returns a Promise, returns a new function
 *  that is an endpoint hook. Its `value` is initially `null` and will be set once
 *  the endpoint has returned a value for it. While loading, `isLoading` is set to
 *  true, and when an error occurs, `error` will contain the returned error.
 *
 * Additionally, `reload` will reload the data.
 *
 * The returned function copies the typing from the input function, so if you define
 *  a proper JSDoc for the input function, the typing will also be available for
 *  the created function.
 *
 * For example, if there's a function:
 * ```js
 * const loadSomeData: (argument: number) => Promise<SomeData>
 * ```
 *
 * Then defining:
 * ```js
 * const useSomeData = createEndpointHook(loadSomeData);
 * ```
 * Will give `useSomeData` the typing `(argument: number) => {value: SomeData?, error?: any, ...}
 *
 * Note that `value` will initially be `defaultValue` or `null` until a value has been loaded.
 */
export var createEndpointHook = function (func, defaultValue) {
    if (defaultValue === void 0) { defaultValue = null; }
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _a = useState({ value: defaultValue, isLoading: true, error: null }), state = _a[0], setState = _a[1];
        // If `load` is called multiple times in a row, we want to ignore values returned by `then` and `catch`
        //  in all but the last call because those results don't apply anymore, since `args` has probably changed.
        // So to solve that, we check whether the latest loadId matches the originating load id before setting the state.
        var loadId = useRef(0);
        var _b = useState(args), rememberedArgs = _b[0], setRememberedArgs = _b[1];
        useEffect(function () {
            // Deep-compare the args for changes instead of a regular instance comparison.
            if (!_.isEqual(args, rememberedArgs)) {
                setRememberedArgs(args);
            }
            // Linting doesn't understand that we depend on all the array's elements.
            // Using just `[args]` causes the state to be re-calculated indefinitely causing the page to crash.
            // But this is the way to go.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, args);
        var load = useCallback(function () {
            var args_1 = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args_1[_i] = arguments[_i];
            }
            return __awaiter(void 0, __spreadArray([], args_1, true), void 0, function (props) {
                var currentLoadId;
                if (props === void 0) { props = {}; }
                return __generator(this, function (_a) {
                    if (props.showLoading === false) {
                        setState(function (state) { return (__assign(__assign({}, state), { isLoading: false, error: null })); });
                    }
                    else {
                        setState({ value: defaultValue, isLoading: true, error: null });
                    }
                    currentLoadId = ++loadId.current;
                    return [2 /*return*/, func.apply(void 0, rememberedArgs).then(function (x) {
                            if (loadId.current === currentLoadId) {
                                setState({ value: x, isLoading: false, error: null });
                            }
                        })
                            .catch(function (e) {
                            if (loadId.current === currentLoadId) {
                                setState({ value: defaultValue, isLoading: false, error: e });
                            }
                        })];
                });
            });
        }, [rememberedArgs]);
        useEffect(function () {
            load();
        }, [load]);
        return __assign(__assign({}, state), { reload: load });
    };
};
/**
 * Similar to {@link createEndpointHook}, but maps the result from {@link action} to its data so that that step
 *  can be skipped.
 */
export var createAxiosEndpointHook = function (action, defaultValue) {
    if (defaultValue === void 0) { defaultValue = null; }
    return createEndpointHook(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return action.apply(void 0, args).then(function (x) { return x.data; });
    }, defaultValue);
};
/**
 * The same as {@link createEndpointHook}(func, []);
 *
 * This can be used with ListPageNavRenderer to display meaningful states.
 */
export var createListEndpointHook = function (func) {
    return createEndpointHook(func, []);
};
/**
 * Combines the states returned by {@link createEndpointHook}
 *  in order to create a single state of the same form, in order to use a SinglePageNavRenderer
 *  to display a loading state while any of them are loading, or an error when any error was returned.
 *
 * The returned `reload` function reloads all the given states. If individual states need to be reloaded,
 *  you can use that method directly instead of using the value returned by this method.
 */
export var combineEndpointStates = function (states) {
    return useMemo(function () {
        var _a, _b;
        return ({
            value: _.mapValues(states, function (state) { return state.value; }),
            isLoading: _.some(states, 'isLoading'),
            error: (_b = (_a = _.find(states, 'error')) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : null,
            reload: function (props) { return __awaiter(void 0, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, Promise.all(_.map(states, function (state) { return state.reload(props); }))];
                        case 1:
                            _a.sent();
                            return [2 /*return*/];
                    }
                });
            }); },
        });
    }, _.values(states));
};
/**
 * Helper function that calculates the diff from an endpoint state.
 *
 * @param state The endpoint state.
 * @param keyIteratee The key selector used to check if two elements are equal.
 */
export var useEndpointDiff = function (state, keyIteratee) {
    return useDiff(state.value, !state.isLoading && _.isNil(state.error), keyIteratee);
};
/**
 * Wrap this around a {@link createListEndpointHook} to track the difference between the previous and current
 *  call.
 *
 * @param hook The endpoint hook.
 * @param keyIteratee The key selector used to check if two elements are equal.
 */
export var withEndpointStateDiff = function (hook, keyIteratee) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var state = hook.apply(void 0, args);
        var diff = useEndpointDiff(state, keyIteratee);
        return __assign(__assign({}, state), { diff: diff });
    };
};
